/* import __COLOCATED_TEMPLATE__ from './cx-benchmark-panel.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import {
  type CxBenchmarkIndustry,
  type CxBenchmarkReasons,
} from 'embercom/components/reporting/bespoke/fin-performance/cx-benchmark-panel/types';
import {
  getCxBenchmarkData,
  getBenchmarkGroup,
  getIndustryDisplayName,
  getAvailableIndustries,
} from 'embercom/lib/reporting/cx-benchmarks/cx-benchmark-service';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';

const FIN_PARTICIPATED_FILTER = {
  type: 'category',
  data: {
    property: 'agent_types_involved_grouping',
    attribute: 'conversation.agent_types_involved_grouping',
    values: ['ai-agent'],
  },
};

const ALL_REASON_VALUES = [
  'answer_quality_bot:high',
  'answer_quality_bot:low',
  'product_feedback:positive',
  'product_feedback:negative',
  'policy_feedback:positive',
  'policy_feedback:negative',
  'user_strong_emotion:positive',
  'user_strong_emotion:negative',
  'user_effort:low',
  'user_effort:high',
];

const ES_KEY_TO_REASON_KEY: Record<string, keyof CxBenchmarkReasons> = {
  'High Fin answer quality': 'answer_quality_bot_high',
  'Low Fin answer quality': 'answer_quality_bot_low',
  'Positive product feedback': 'product_feedback_positive',
  'Negative product feedback': 'product_feedback_negative',
  'Positive policy feedback': 'policy_feedback_positive',
  'Negative policy feedback': 'policy_feedback_negative',
  'Positive user emotion': 'user_strong_emotion_positive',
  'Negative user emotion': 'user_strong_emotion_negative',
  'Low user effort': 'user_effort_low',
  'High user effort': 'user_effort_high',
};

interface Signature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    appCxScore: number | undefined;
    reportState: ReportState;
  };
}

export default class CxBenchmarkPanel extends Component<Signature> {
  @service declare intl: IntlService;
  @tracked selectedIndustry: CxBenchmarkIndustry = 'overall';

  @use private reasonCountsResource = staticDataResource(this, {
    serializedChart: () => this.reasonCountsChartDefinition,
    reportState: () => this.args.reportState,
  });

  @use private totalCountResource = staticDataResource(this, {
    serializedChart: () => this.totalCountChartDefinition,
    reportState: () => this.args.reportState,
  });

  get benchmarkData() {
    return getCxBenchmarkData();
  }

  get appSegment(): string {
    return this.benchmarkData.app_segment ?? 'overall';
  }

  get selectedBenchmarkGroup() {
    return getBenchmarkGroup(this.benchmarkData, this.selectedIndustry, this.appSegment);
  }

  get selectedIndustryLabel() {
    return getIndustryDisplayName(this.intl, this.selectedIndustry);
  }

  get industryItems() {
    return getAvailableIndustries(this.benchmarkData).map((industry) => ({
      text: getIndustryDisplayName(this.intl, industry),
      value: industry,
    }));
  }

  private get reasonCountsChartDefinition() {
    return {
      visualization_type: 'horizontal-bar',
      view_by: 'conversation_custom_fields#customer_experience_attributes',
      chart_series: [
        {
          metric_id: 'conversation.ai_generated_metrics.csat.count',
          aggregation: 'count',
          show_data_labels: false,
          filters: {
            type: 'and',
            filters: [
              FIN_PARTICIPATED_FILTER,
              {
                type: 'category',
                data: {
                  property: 'conversation_custom_fields#customer_experience_attributes',
                  attribute: 'conversation_custom_fields#customer_experience_attributes',
                  values: ALL_REASON_VALUES,
                },
              },
            ],
          },
        },
      ],
    };
  }

  private get totalCountChartDefinition() {
    return {
      visualization_type: 'counter',
      chart_series: [
        {
          metric_id: 'conversation.ai_generated_metrics.csat.count',
          aggregation: 'count',
          filters: {
            type: 'and',
            filters: [FIN_PARTICIPATED_FILTER],
          },
        },
      ],
    };
  }

  get appReasonRates(): CxBenchmarkReasons | undefined {
    if (this.reasonCountsResource.isLoading || this.totalCountResource.isLoading) {
      return undefined;
    }

    let totalKpi = this.totalCountResource.kpiData;
    if (!totalKpi || !totalKpi[0]) {
      return undefined;
    }
    let totalCount = totalKpi[0] as number;
    if (totalCount === 0) {
      return undefined;
    }

    let reasonCounts = this.reasonCountsResource.flatCombinedData;

    let rates: Partial<CxBenchmarkReasons> = {};
    for (let [displayName, reasonKey] of Object.entries(ES_KEY_TO_REASON_KEY)) {
      let countArray = reasonCounts[displayName];
      let count = countArray && countArray[0] ? (countArray[0] as number) : 0;
      rates[reasonKey] = count / totalCount;
    }

    return rates as CxBenchmarkReasons;
  }

  @action
  onSelectIndustry(industry: CxBenchmarkIndustry) {
    this.selectedIndustry = industry;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::FinPerformance::CxBenchmarkPanel': typeof CxBenchmarkPanel;
    'reporting/bespoke/fin-performance/cx-benchmark-panel': typeof CxBenchmarkPanel;
  }
}
